import React, { useCallback, useEffect, useState } from "react";
import { HardDrive, Database, CheckCircle2, Loader2 } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./Storage.module.scss";

const formatGB = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  const num = Number(value);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Storage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchStorage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/billing/storage/me");
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load storage details",
          }),
        );
      }
      setData(res.data || null);
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load storage details"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStorage();
  }, [fetchStorage]);

  const percent = Math.max(0, Math.min(100, Number(data?.percentUsed ?? 0)));
  const trackTone =
    percent >= 90 ? "danger" : percent >= 70 ? "warning" : "primary";

  return (
    <div className={styles["storage__page"]}>
      {notification && (
        <div className={styles["storage__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["storage__header"]}>
        <div className={styles["storage__icon-wrapper"]}>
          <HardDrive size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["storage__title-group"]}>
          <h2 className={styles["storage__title"]}>Storage</h2>
          <p className={styles["storage__subtitle"]}>
            Monitor your organization’s storage consumption.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["storage__loading"]}>
          <Loader2 className={styles["storage__spin"]} size={28} />
          <span>Loading storage details…</span>
        </div>
      ) : data ? (
        <>
          {/* SUMMARY CARD */}
          <div className={styles["storage__summary"]}>
            <div className={styles["storage__summary-head"]}>
              <span className={styles["storage__summary-label"]}>
                Storage Used
              </span>
              <span className={styles["storage__summary-percent"]}>
                {formatGB(percent)}%
              </span>
            </div>

            <div
              className={`${styles["storage__progress"]} ${
                styles[`storage__progress--${trackTone}`]
              }`}
            >
              <div
                className={styles["storage__progress-fill"]}
                style={{ width: `${percent}%` }}
              />
            </div>

            <div className={styles["storage__summary-foot"]}>
              <strong>{formatGB(data.storageUsedGb)} GB</strong>
              <span> used of </span>
              <strong>{formatGB(data.storageLimitGb)} GB</strong>
            </div>
          </div>

          {/* STAT TILES */}
          <div className={styles["storage__tiles"]}>
            <StatTile
              icon={<Database size={20} strokeWidth={2.5} />}
              label="Total Limit"
              value={`${formatGB(data.storageLimitGb)} GB`}
              tone="primary"
            />
            <StatTile
              icon={<HardDrive size={20} strokeWidth={2.5} />}
              label="Used"
              value={`${formatGB(data.storageUsedGb)} GB`}
              tone="warning"
            />
            <StatTile
              icon={<CheckCircle2 size={20} strokeWidth={2.5} />}
              label="Available"
              value={`${formatGB(
                Number(data.storageAvailableBytes) / 1_000_000_000,
              )} GB`}
              tone="success"
            />
          </div>

          {/* META */}
          <div className={styles["storage__meta"]}>
            <span>Last updated</span>
            <strong>{formatDateTime(data.updatedAt)}</strong>
          </div>
        </>
      ) : null}
    </div>
  );
};

const StatTile = ({ icon, label, value, tone }) => (
  <div
    className={`${styles["storage__tile"]} ${styles[`storage__tile--${tone}`]}`}
  >
    <div className={styles["storage__tile-icon"]}>{icon}</div>
    <div className={styles["storage__tile-info"]}>
      <span className={styles["storage__tile-label"]}>{label}</span>
      <span className={styles["storage__tile-value"]}>{value}</span>
    </div>
  </div>
);

export default Storage;
